import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

// ─────────────────────────────────────────────────────────────
// RESET PASSWORD PAGE
// Opened when the user clicks the "Reset Password" button/link
// inside the email sent from the Forgot Password flow.
// Expected URL: /reset-password?key=<UserKey>&email=<UserEmail>
// ─────────────────────────────────────────────────────────────
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const userKey = searchParams.get("key") || "";
  const userEmail = searchParams.get("email") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!userKey || !userEmail) {
      setMessage("❌ Invalid or expired reset link.");
    }
  }, [userKey, userEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setSuccess(false);

    if (!userKey || !userEmail) {
      setMessage("❌ Invalid or expired reset link.");
      return;
    }

    if (newPassword.length < 6) {
      setMessage("❌ Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("❌ Passwords do not match.");
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Yahan apna actual "change/reset password" API endpoint lagaiye.
      // Filhaal koi reset-password API URL nahi mila hai, isliye placeholder
      // hai — same pattern follow kiya hai jaisa Login/UserDetails API me hai.
      const apiUrl =
        process.env.REACT_APP_API_RESET_PASSWORD_URL ||
        "https://promo.namantechnolab.com/Accounts/ResetPassword";

      const payload = {
        UserEmail: userEmail,
        UserKey: userKey,
        NewPassword: newPassword,
        AgencyKey: "0EBB4DDF-1C96-44C7-81A4-98A74DDB3611",
        AccountNo: 11,
      };

      console.log("🔑 Reset Password Request URL:", apiUrl);
      console.log("📨 Sending Payload:", JSON.stringify(payload, null, 2));

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const data = await response.json();
      console.log("✅ Reset Password Response:", JSON.stringify(data, null, 2));

      if (data && (data.ApiStatus === 1 || data.UserStatus === "Valid" || data.Success === true)) {
        setSuccess(true);
        setMessage("✅ Your password has been reset successfully. You can now login.");
        setNewPassword("");
        setConfirmPassword("");

        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        setSuccess(false);
        let errorMsg = "Couldn't reset password. The link may have expired.";
        if (data?.ApiMessage) errorMsg = data.ApiMessage;
        else if (data?.message) errorMsg = data.message;
        else if (data?.error) errorMsg = data.error;
        setMessage("❌ " + errorMsg);
      }
    } catch (error) {
      console.error("❌ RESET PASSWORD ERROR:", error.message);
      setSuccess(false);
      if (error.message.includes("Failed to fetch")) {
        setMessage("❌ Network error! Check your internet connection.");
      } else if (error.message.includes("HTTP")) {
        setMessage(`❌ Server error: ${error.message}`);
      } else {
        setMessage("❌ " + error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reset-pw-scope">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght=300;400;600&display=swap');
        @import url('https://unpkg.com/boxicons@2.1.4/css/boxicons.min.css');

        .reset-pw-scope * {
          box-sizing: border-box;
          font-family: "Poppins", sans-serif;
        }

        .reset-pw-scope {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          padding: 16px;
        }

        .reset-pw-card {
          width: 100%;
          max-width: 420px;
          background: #fff;
          border-radius: 16px;
          padding: 36px 32px;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0,0,0,0.08);
        }

        .reset-pw-card h1 {
          font-size: 26px;
          margin-bottom: 6px;
          color: #111827;
        }

        .reset-pw-card p.subtitle {
          font-size: 13.5px;
          color: #6b7280;
          margin-bottom: 20px;
        }

        .reset-pw-card .input-box {
          position: relative;
          width: 100%;
          margin: 14px 0;
        }

        .reset-pw-card .input-box input {
          width: 100%;
          padding: 13px 50px 13px 20px;
          background: #eee;
          border-radius: 8px;
          border: none;
          outline: none;
          font-size: 16px;
          color: #333;
          font-weight: 500;
        }

        .reset-pw-card .input-box input:focus {
          background: #fff;
          border: 2px solid #0a2a6e;
        }

        .reset-pw-card .input-box i {
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 20px;
          color: #6b7280;
        }

        .reset-pw-card .btn {
          width: 100%;
          height: 48px;
          background: #0a2a6e;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-size: 16px;
          color: #fff;
          font-weight: 600;
          margin-top: 12px;
          transition: 0.3s;
        }

        .reset-pw-card .btn:hover {
          background: #082050;
        }

        .reset-pw-card .btn:disabled {
          background: #97a3c4;
          cursor: not-allowed;
        }

        .reset-pw-card .msg-text {
          font-weight: 600;
          margin-top: 15px;
          font-size: 14px;
        }

        .reset-pw-card .msg-text.success { color: #4caf50; }
        .reset-pw-card .msg-text.error { color: #d32f2f; }

        .reset-pw-card .back-link {
          margin-top: 18px;
          font-size: 13.5px;
          color: #0a2a6e;
          font-weight: 600;
          cursor: pointer;
        }

        .reset-pw-card .back-link:hover {
          text-decoration: underline;
        }
      `}</style>

      <div className="reset-pw-card">
        <h1>Set New Password</h1>
        <p className="subtitle">
          {userEmail ? `Resetting password for ${userEmail}` : "Enter your new password below"}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="input-box">
            <input
              type="password"
              placeholder="New Password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isSubmitting || !userKey}
            />
            <i className="bx bxs-lock-alt"></i>
          </div>

          <div className="input-box">
            <input
              type="password"
              placeholder="Confirm New Password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting || !userKey}
            />
            <i className="bx bxs-lock-alt"></i>
          </div>

          <button type="submit" className="btn" disabled={isSubmitting || !userKey}>
            {isSubmitting ? "Updating..." : "Reset Password"}
          </button>

          {message && (
            <p className={`msg-text ${success ? "success" : "error"}`}>{message}</p>
          )}

          <p className="back-link" onClick={() => navigate("/")}>
            ← Back to Home
          </p>
        </form>
      </div>
    </div>
  );
}